import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Box,
  Button, 
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import CloudIcon from '@mui/icons-material/Cloud';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../../contexts/AuthContext';
import { SignInButton } from '../auth/SignInButton';
import { UserMenu } from '../auth/UserMenu';

/**
 * Header component with app title, plans dropdown, and import button
 */
const Header = ({ onImportClick, onTitleClick, fileInputRef, plans = [], onNewPlan, onManageStorage }) => {
  const { user } = useAuth();
  const [plansAnchorEl, setPlansAnchorEl] = useState(null);
  const plansMenuOpen = Boolean(plansAnchorEl);

  const handlePlansMenuClick = (event) => {
    setPlansAnchorEl(event.currentTarget);
  };

  const handlePlansMenuClose = () => {
    setPlansAnchorEl(null);
  };

  const handleNewPlan = () => {
    handlePlansMenuClose();
    if (onNewPlan) onNewPlan();
  };

  const handleManageStorage = () => {
    handlePlansMenuClose();
    if (onManageStorage) onManageStorage();
  };

  const localPlans = plans.filter(p => !p.isCloudStored);
  const cloudPlans = plans.filter(p => p.isCloudStored);

  return (
    <AppBar 
      position="static" 
      elevation={0}
    >
      <Toolbar>
        <Box 
          component="img" 
          src="/ultrapackr.png" 
          alt="UltraPackr"
          sx={{ 
            height: 32,
            width: 'auto',
            mr: 1,
            cursor: 'pointer',
            filter: 'brightness(0) invert(1)',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': { 
              opacity: 0.9,
              transform: 'scale(1.05)'
            }
          }}
          onClick={onTitleClick}
        />
        <Chip 
          label="BETA" 
          size="small" 
          color="secondary"
          sx={{ 
            ml: 0.5,
            mr: 'auto',
            fontSize: '0.65rem', 
            height: '20px',
            fontWeight: 'bold',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            color: 'white',
            '& .MuiChip-label': { 
              px: 1,
            }
          }}
        />

        {/* Plans Dropdown Menu */}
        <Button
          variant="text"
          endIcon={<KeyboardArrowDownIcon />}
          onClick={handlePlansMenuClick}
          sx={{ 
            ml: 2,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          Plans
        </Button>
        <Menu
          anchorEl={plansAnchorEl}
          open={plansMenuOpen}
          onClose={handlePlansMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
            }
          }}
        >
          <MenuItem onClick={handleNewPlan}>
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>New Plan</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem disabled sx={{ opacity: '1 !important', cursor: 'default !important' }}>
            <ListItemIcon>
              <FolderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary={`Local Plans (${localPlans.length})`}
              primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
            />
          </MenuItem>
          <MenuItem disabled sx={{ opacity: '1 !important', cursor: 'default !important' }}>
            <ListItemIcon>
              <CloudIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary={`Cloud Plans (${cloudPlans.length})`}
              primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
            />
          </MenuItem>
          {onManageStorage && (
            <>
              <Divider />
              <MenuItem onClick={handleManageStorage}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Manage Storage</ListItemText>
              </MenuItem>
            </>
          )}
        </Menu>

        <Button
          variant="outlined"
          startIcon={<FileUploadIcon />}
          onClick={onImportClick}
          sx={{ 
            ml: 1,
            whiteSpace: 'nowrap',
            minWidth: 'fit-content',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            color: 'white',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          Import Plan
        </Button>

        {/* Authentication UI */}
        <Box sx={{ ml: 2 }}>
          {user ? <UserMenu /> : <SignInButton />}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header; 